import { BitkitButton, BitkitDialog, BitkitLink, BitkitList, IconCheck } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Text } from '@chakra-ui/react/text';
import { differenceInDays, differenceInHours } from 'date-fns';
import { useContext } from 'react';

import CopiableInput from '@/components/CopiableInput/CopiableInput';
import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import { GlobalPropsWithAccount } from '@/models/GlobalProps';
import { getWorkspaceTeamPath } from '@/utils/monolithApiRouteService';

interface NotifyWorkspaceOwnerDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const NotifyWorkspaceOwnerDialog = ({ isOpen, onClose }: NotifyWorkspaceOwnerDialogProps) => {
  const {
    account: {
      slug: accountSlug,
      buildCacheTrial: trial,
      hasBuildCacheSubscription: hasSubscription,
      buildCacheConsumption,
    },
  } = useContext(GlobalPropsContext) as GlobalPropsWithAccount;

  const copiableInputValue = `https://app.bitrise.io/workspaces/${accountSlug}/settings/billing/build-cache-plan`;
  let dialogTitle;
  let topBodyText;
  let shouldSubscribe = false;

  if (hasSubscription) {
    shouldSubscribe = true;

    const isOverlimits =
      buildCacheConsumption &&
      buildCacheConsumption.currentInvocationCount > buildCacheConsumption.invocationCountLimit;
    const hasReachedThreeQuartersOfLimit =
      buildCacheConsumption &&
      buildCacheConsumption.currentInvocationCount >= buildCacheConsumption.invocationCountLimit * 0.75;

    if (isOverlimits) {
      dialogTitle = 'Upgrade your plan to continue using Build Cache';
      topBodyText = (
        <>
          You have exceeded your Build Cache quota. To keep benefiting from faster builds and a smoother workflow,
          upgrade to a <b>Pro plan</b>.
        </>
      );
    } else if (hasReachedThreeQuartersOfLimit) {
      dialogTitle = 'Upgrade your plan to continue using Build Cache';
      topBodyText = (
        <>
          You are close to reaching your Build Cache quota. Upgrade to a <b>Pro plan</b> now to avoid slowdowns and keep
          builds fast.
        </>
      );
    } else {
      return null;
    }
  } else if (trial) {
    if (trial.isActive) {
      dialogTitle = 'Subscribe to a plan to continue using Build Cache';

      const isTrialEndsInSevenDays = differenceInDays(new Date(trial.expiresAt), new Date()) === 7;
      const isTrialEndsInOneDay = differenceInHours(new Date(trial.expiresAt), new Date()) <= 24;

      if (isTrialEndsInSevenDays) {
        topBodyText = (
          <>
            Your Build Cache is ending in <b>7 days</b>. To keep saving valuable dev time, you’ll need a Pro plan.
          </>
        );
      } else if (isTrialEndsInOneDay) {
        topBodyText = (
          <>
            Your Build Cache is ending in <b>less than a day</b>. To keep saving valuable dev time, you’ll need a Pro
            plan.
          </>
        );
      } else {
        return null;
      }
    } else {
      dialogTitle = 'Subscribe to a plan to continue using Build Cache';
      topBodyText = (
        <>
          Your Build Cache trial has ended. To keep saving valuable dev time, you’ll need a <b>Pro plan</b>.
        </>
      );
    }
  } else return null;

  return (
    <BitkitDialog
      open={isOpen}
      title={dialogTitle}
      onOpenChange={({ open }) => {
        if (!open) onClose();
      }}
      footerButtons={
        <BitkitButton variant="secondary" size="lg" onClick={onClose}>
          Dismiss
        </BitkitButton>
      }
    >
      {/* BitkitDialog doesn't spread rest props onto Dialog.Content, so data-* attributes have to live on the body. */}
      <Box
        display="flex"
        flexDir="column"
        gap="24"
        data-testid="notify-workspace-owner-dialog"
        data-clarity-unmask="true"
      >
        <Box display="flex" flexDir="column" gap="16">
          <Text textStyle="body/lg/regular" color="text/body">
            {topBodyText}
          </Text>
          <BitkitList>
            <BitkitList.Item icon={IconCheck} iconColor="icon/positive">
              Up to 90% faster builds.
            </BitkitList.Item>
            <BitkitList.Item icon={IconCheck} iconColor="icon/positive">
              Fully managed — no setup or maintenance.
            </BitkitList.Item>
            <BitkitList.Item icon={IconCheck} iconColor="icon/positive">
              Deliver faster, reduce team frustration.
            </BitkitList.Item>
          </BitkitList>
        </Box>
        <Box display="flex" flexDir="column" gap="12">
          <Text textStyle="body/md/regular" color="text/secondary">
            To {shouldSubscribe ? 'subscribe to a' : 'upgrade your'} plan, send the URL below to one of your{' '}
            <BitkitLink
              colorVariant="purple"
              href={getWorkspaceTeamPath(accountSlug, {
                params: {
                  tab: 'owners',
                },
              })}
              isExternal
            >
              workspace owners
            </BitkitLink>
            .
          </Text>
          <CopiableInput hideButtonText value={copiableInputValue} />
        </Box>
      </Box>
    </BitkitDialog>
  );
};

export default NotifyWorkspaceOwnerDialog;
